// PushPermissionContent.tsx
// Content component for push notification permission modal (to be used with useModal)

import React, { useState } from "react";
import { Button } from "components/Button";
import { BTN_SIZE, BTN_VARIANTS } from "constants/components";
import parse from "html-react-parser";
import { Image } from "@/components/Image";
import notificationIcon from "@/assets/images/notification_bell.svg";
import { NOTIFICATION } from "@/constants/notification";
import styles from "./PushPermissionContent.module.scss";

export interface PushPermissionContentConfig {
  title?: string;
  description?: any;
  allowButtonText?: string;
  rejectButtonText?: string;
}

interface PushPermissionContentProps {
  onAllow: () => void;
  onReject: () => void;
  content?: PushPermissionContentConfig;
}

/**
 * Content for requesting push notification permission
 * To be rendered inside useModal()
 */
export const PushPermissionContent: React.FC<PushPermissionContentProps> = ({
  onAllow,
  onReject,
  content = {
    title: NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_TITLE,
    description: NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_DESCRIPTION,
    allowButtonText:
      NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_ALLOW_BUTTON_TEXT,
    rejectButtonText:
      NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_REJECT_BUTTON_TEXT,
  },
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAllow = () => {
    setIsLoading(true);
    onAllow();
  };

  return (
    <div className={styles.push_permission_content}>
      {/* Icon */}
      <div className={styles.push_permission_icon}>
        <Image source={notificationIcon} />
      </div>

      {/* Title */}
      <h2 className={styles.push_permission_title}>{content.title}</h2>

      {/* Description */}
      <div className={styles.push_permission_description}>
        {parse(content?.description)}
      </div>

      {/* Buttons */}
      <div className={styles.push_permission_buttons}>
        <Button
          type={BTN_VARIANTS.PRIMARY}
          size={BTN_SIZE.LARGE}
          onClick={handleAllow}
          loading={isLoading}
          disabled={isLoading}
        >
          {content.allowButtonText}
        </Button>
        <Button
          type={BTN_VARIANTS.SECONDARY}
          size={BTN_SIZE.LARGE}
          onClick={onReject}
          disabled={isLoading}
        >
          {content.rejectButtonText}
        </Button>
      </div>
    </div>
  );
};
